<template>
  <div class="container">
      <div>
          <navBar v-bind:class="{hidden: !isDesktop}"/>
          <MobileNavbar v-bind:class="{hidden: isDesktop}"/>
      </div>
      <div>
      <div>
        <!-- ── Homepage hero ──────────────────────────────────── -->
        <section class="bst-hero" aria-label="BarbershopTalk introduction">
          <span class="bst-hero__kicker">
            <i class="fa fa-shield-alt" aria-hidden="true"></i>
            Daily Intelligence Platform
          </span>
          <h1 class="bst-hero__h1">News. Every perspective. No filter.</h1>
          <p class="bst-hero__sub">
            <span class="bst-hero__sub-line">See what's trending and how articles lean.</span>
            <span class="bst-hero__sub-line bst-hero__sub-line--desktop">&#32;Track every perspective. Catch what your side is missing.</span>
          </p>
          <p class="bst-hero__tagline">The conversation starts here.</p>
          <div class="bst-hero__actions">
            <router-link
              v-if="!currentUser"
              class="bst-hero__cta bst-hero__cta--primary"
              :to="{ name: 'register' }"
              aria-label="Sign up for free and get the BarbershopTalk Daily Cut newsletter"
            >
              <span class="bst-hero__cta-label bst-hero__cta-label--desktop">Get the Daily Cut Free</span>
              <span class="bst-hero__cta-label bst-hero__cta-label--mobile">Sign Up Free</span>
              <i class="fa fa-arrow-right" aria-hidden="true"></i>
            </router-link>
            <router-link
              v-else
              class="bst-hero__cta bst-hero__cta--primary"
              :to="{ name: 'newsletterPage' }"
            >
              <span class="bst-hero__cta-label bst-hero__cta-label--desktop">Read Today's Brief</span>
              <span class="bst-hero__cta-label bst-hero__cta-label--mobile">Today's Brief</span>
              <i class="fa fa-arrow-right" aria-hidden="true"></i>
            </router-link>
            <router-link
              class="bst-hero__cta bst-hero__cta--ghost"
              :to="{ name: 'political-intelligence' }"
              aria-label="Explore today's political intelligence"
            >
              <span class="bst-hero__cta-label bst-hero__cta-label--desktop">Explore today's politics</span>
              <span class="bst-hero__cta-label bst-hero__cta-label--mobile">Political Brief</span>
            </router-link>
          </div>
          <div class="bst-hero__chips" aria-label="Platform features">
            <span><i class="fa fa-chart-line" aria-hidden="true"></i> Media Pulse</span>
            <span><i class="fa fa-eye-slash" aria-hidden="true"></i> Blindspot Detector</span>
            <router-link :to="{ name: 'sniperPicksPage' }" class="bst-hero__chip-link" aria-label="See today's picks">
              <span><i class="fa fa-crosshairs" aria-hidden="true"></i> Under Sniper</span>
            </router-link>
          </div>
        </section>
          <div id="media-pulse" class="pulse-section">
            <p class="pulse-intro">
              <strong>Media Pulse, </strong>
              <span class="pulse-intro__full">See whether coverage is turning positive, negative, or chaotic across the stories people are reading right now.</span>
              <span class="pulse-intro__short">Today's emotional temperature across the stories people are reading.</span>
            </p>
            <SentimentPulseDashboard />
          </div>
          <!-- ── Intelligence feature strip ──────────────────────── -->
          <div id="home-intelligence" class="home-intelligence-strip">
            <router-link class="home-intelligence-strip__card" :to="{ name: 'newsletterPage' }">
              <i class="fa fa-scissors home-intelligence-strip__icon" aria-hidden="true"></i>
              <div class="home-intelligence-strip__text">
                <strong>The Daily Cut</strong>
                <span>What happened today, how it was framed, and what to think about it.</span>
              </div>
              <i class="fa fa-arrow-right home-intelligence-strip__arrow" aria-hidden="true"></i>
            </router-link>
            <router-link class="home-intelligence-strip__card" :to="{ name: 'political-intelligence' }">
              <i class="fa fa-eye-slash home-intelligence-strip__icon" aria-hidden="true"></i>
              <div class="home-intelligence-strip__text">
                <strong>Blindspot Detector</strong>
                <span>What the left, right, and center aren't all saying.</span>
              </div>
              <i class="fa fa-arrow-right home-intelligence-strip__arrow" aria-hidden="true"></i>
            </router-link>
            <div class="home-intelligence-strip__card home-intelligence-strip__card--picks">
              <i class="fa fa-crosshairs home-intelligence-strip__icon home-intelligence-strip__icon--picks" aria-hidden="true"></i>
              <div class="home-intelligence-strip__text">
                <strong>The Under Sniper</strong>
                <span>Under Sniper analyzes sports markets and publishes only the picks that meet its standards.</span>
                <div class="home-intelligence-strip__actions">
                  <router-link :to="{ name: 'sniperPicksPage' }">See today's picks</router-link>
                  <router-link :to="{ name: 'underSniperPage' }">How it works</router-link>
                </div>
              </div>
            </div>
          </div>

          <LoadingOverlay v-if="isLoading" aria-label="Loading content" />
          
          <!-- Pull-to-refresh indicator -->
          <div
            v-show="ptrIsRefreshing"
            class="pull-refresh-indicator"
            aria-live="polite"
            aria-busy="true"
          >
            <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
            <span class="refresh-text">Refreshing...</span>
          </div>
          
          <div class="main-area" v-if="!isLoading">
          
            <!-- Trending Sidebar -->
            <div id = "trending">
              <div> 
              <label for="home-search" class="sr-only">Search articles</label>
              <input id="home-search" class="search-bar" type="text" placeholder="Search Through News Titles..." v-model="term" v-on:keyup.enter="searchArticles(term)" aria-label="Search through news titles"> 
              <span> <i class="fa-solid fa-magnifying-glass fa-lg" ></i> </span>                
            </div>
              <h3 class="trending-sidebar-heading">Trending Articles</h3>
              <!-- for loop to iterate through the trending articles array -->
            <div class = "trending-articles"  v-for="articles in trendingArticles" v-bind:key="articles.articleId">
            <div class="trending-information-section">
              <div class="trending-title-container">
                <a :href="articles.urlPath || `/${articles.categorySpecified != null ? articles.categorySpecified : 'business'}/${(articles.title || '').toLowerCase().replace(/\W+/g, '-').replace(/^-|-$/g, '')}/${articles.articleId}`" :aria-label="articles.viewerState?.liked ? 'Unlike this article' : 'Like this article'" @click.prevent="pushToArticle(articles.urlPath || articles.categorySpecified, articles.title, articles.articleId)">
                  <p class = "trending-title" v-on:click="postClicked=article.id; logArticleClick()">{{articles.title}}</p>
                </a>
          </div>
            </div>
            <div class="trending-data">
            <span class = "trending-category">{{ articles.category }}</span>
            <!-- only going to show likes for trending articles -->
              <span class = "trending-like-count">Likes<button aria-label="like-button" v-on:click="handleLikeClick(articles.articleId)"><i class="fa fa-fire" v-bind:class="{'i-changed': articles.viewerState?.liked}"></i></button>: {{ articles.likeCount }}</span>
            </div>
            <span class = "trending-published">On: {{ articles.publishedAt }}</span>
          </div>
       
            </div>

            <!-- main news grid, v-if relates to the buttons press in mobile view -->
            <div class="home-main-content-column">
              <TrendingStoriesRail
                title="What the Media Is All Covering Right Now"
                subtitle="Stories gaining multi-source momentum, every angle, one place."
                :limit="10"
              />

              <!-- ── Feed tabs (above active feed) ─────────────────────── -->
              <div id="home-feed" class="home-feed-tabs">
                <button
                  class="home-feed-tab"
                  :class="{ 'home-feed-tab--active': feedTab === 'news' }"
                  @click="setFeedTab('news')"
                >All News</button>
                <router-link
                  v-if="!currentUser"
                  class="home-feed-tab home-feed-tab--cta"
                  :to="{ name: 'register' }"
                  aria-label="Create a free account to personalize your feed"
                >
                  Personalize My Feed
                </router-link>
                <button
                  v-if="currentUser"
                  class="home-feed-tab"
                  :class="{ 'home-feed-tab--active': feedTab === 'content-based' }"
                  @click="setFeedTab('content-based')"
                >For You</button>
                <button
                  v-if="currentUser"
                  class="home-feed-tab"
                  :class="{ 'home-feed-tab--active': feedTab === 'your-topics' }"
                  @click="setFeedTab('your-topics')"
                >Your Topics</button>
              </div>

              <NotLoggedIn v-if="!currentUser"></NotLoggedIn>
              <FeedIsLoading v-if="feedTab === 'content-based' && currentUser > 0 && curatedContentBasedArticles.length === 0"></FeedIsLoading>
              <ArticleGrid
                v-if="feedTab === 'content-based' && curatedContentBasedArticles.length > 0"
                :articles="displayArticles"
                :currentUser="currentUser"
                :isDesktop="isDesktop"
                @like="handleLikeClick"
                @dislike="handleDislikeClick"
                @save="handleSaveForLaterClick"
                @navigate="pushToArticle"
                @article-click="onArticleClick"
              />
              <!-- main news grid, v-if relates to the buttons press in mobile view -->
            <div class = "news-grid" v-show="feedTab === 'news'" :class="{ hidden: !isDesktop }">
              <!-- all articles in currect subject data -->
              <div class = "article" v-for="article in displayArticles" v-bind:key="article.id">
                <div>
                  <!-- <img v-if="article.urlToImageCloudinary !== null" class="article-image" :src=article.urlToImageCloudinary alt="article-image" preload="auto"> -->
                  <img class="article-image" v-lazy="gridArticleThumb(article.imageUrl)" :alt="article.title || 'News article'" preload="auto">
                  <p class="category">published: {{ formatPublished(article.publishedAt) }}</p>
                </div>
                <a :href="article.urlPath" @click.prevent="pushToArticle(article.urlPath)">
                  <h3 class="title" v-on:click="postClicked=article.id; logArticleClick()">{{article.title}}</h3>
                </a>
                <div
                  v-if="getArticleSentimentToken(article) || getArticleLeanToken(article)"
                  class="article-intel-row"
                >
                  <span
                    v-if="getArticleSentimentToken(article)"
                    class="article-intel-chip"
                    :style="getChipStyle(getArticleSentimentToken(article))"
                    :aria-label="`Tone: ${getArticleSentimentToken(article).label}`"
                  >
                    <i :class="['fa', getArticleSentimentToken(article).icon, 'article-intel-chip__icon']" aria-hidden="true"></i>
                    {{ getArticleSentimentToken(article).label }}
                    <span v-if="isMockSentiment(article)" class="article-intel-chip__mock" aria-hidden="true" title="Dev preview">·</span>
                  </span>
                  <span
                    v-if="getArticleLeanToken(article)"
                    class="article-intel-chip article-intel-chip--bias"
                    :style="getChipStyle(getArticleLeanToken(article))"
                    :aria-label="getFramingChipAria(article)"
                  >
                    <span class="article-intel-chip__prefix" aria-hidden="true">Framing:</span>
                    {{ getDisplayBias(article) }}
                    <span v-if="isMockBias(article)" class="article-intel-chip__mock" aria-hidden="true" title="Dev preview">·</span>
                  </span>
                </div>
                <!-- likes dislikes and save for later buttons -->
                <div class = "buttons">
                  <!-- buttons for not logged in -->
                  <a href="/register" aria-label="Create a free account to like articles"><button aria-label="Like this article" v-if="!currentUser"><i class="fa fa-thumbs-up fa-regular"></i></button></a>
        <a href="/register" aria-label="Create a free account to dislike articles"><button aria-label="Dislike this article" v-if="!currentUser"><i class="fa fa-thumbs-down fa-regular"></i></button></a>
        <a href="/register" aria-label="Create a free account to save articles"><button aria-label="Save this article" v-if="!currentUser"><i class="fa fa-regular fa-bookmark"></i></button></a>
                  <!-- buttons for social -->
                  <button v-if="currentUser" :aria-label="article.viewerState?.liked ? 'Unlike this article' : 'Like this article'" v-on:click="handleLikeClick(article.id)"><i class="fa fa-thumbs-up fa-regular" v-bind:class="{'i-changed': article.viewerState?.liked}"></i></button>
                  <button v-if="currentUser" :aria-label="article.viewerState?.disliked ? 'Undislike this article' : 'Dislike this article'" v-on:click="handleDislikeClick(article.id)"><i class="fa-regular fa-thumbs-down" v-bind:class="{'i-changed': article.viewerState?.disliked}"></i></button>
                  <button v-if="currentUser" :aria-label="article.viewerState?.savedForLater ? 'Unsave this article' : 'Save this article'" v-on:click="handleSaveForLaterClick(article.id)"><i class="fa fa-regular fa-bookmark" v-bind:class="{'i-changed': article.viewerState?.savedForLater}"></i></button>
                </div>
              </div>
            </div>
              <div class="news-grid" v-show="feedTab === 'news'" :class="{ hidden: isDesktop }">
  <!-- Column 1 -->
  <div class="column">
    <div class="article" v-for="article in articlesColumn1" v-bind:key="article.id">
      <div>
        <img class="article-image" v-lazy="gridArticleThumb(article.imageUrl)" :alt="article.title || 'News article'" preload="auto">
        <p class="category">published: {{ formatPublished(article.publishedAt) }}</p>
      </div>
      <a :href="article.urlPath" @click.prevent="pushToArticle(article.urlPath)">
        <h3 class="title" v-on:click="postClicked=article.id; logArticleClick()">{{article.title}}</h3>
      </a>
      <div
        v-if="getArticleSentimentToken(article) || getArticleLeanToken(article)"
        class="article-intel-row"
      >
        <span
          v-if="getArticleSentimentToken(article)"
          class="article-intel-chip"
          :style="getChipStyle(getArticleSentimentToken(article))"
          :aria-label="`Tone: ${getArticleSentimentToken(article).label}`"
        >
          <i :class="['fa', getArticleSentimentToken(article).icon, 'article-intel-chip__icon']" aria-hidden="true"></i>
          {{ getArticleSentimentToken(article).label }}
          <span v-if="isMockSentiment(article)" class="article-intel-chip__mock" aria-hidden="true" title="Dev preview">·</span>
        </span>
        <span
          v-if="getArticleLeanToken(article)"
          class="article-intel-chip article-intel-chip--bias"
          :style="getChipStyle(getArticleLeanToken(article))"
:aria-label="getFramingChipAria(article)"
          >
            <span class="article-intel-chip__prefix" aria-hidden="true">Framing:</span>
            {{ getDisplayBias(article) }}
            <span v-if="isMockBias(article)" class="article-intel-chip__mock" aria-hidden="true" title="Dev preview">·</span>
          </span>
      </div>
      <!-- likes dislikes and save for later buttons -->
      <div class="buttons">
        <a href="/register" aria-label="Create a free account to like articles"><button aria-label="Like this article" v-if="!currentUser"><i class="fa fa-thumbs-up fa-regular"></i></button></a>
        <a href="/register" aria-label="Create a free account to dislike articles"><button aria-label="Dislike this article" v-if="!currentUser"><i class="fa fa-thumbs-down fa-regular"></i></button></a>
        <a href="/register" aria-label="Create a free account to save articles"><button aria-label="Save this article" v-if="!currentUser"><i class="fa fa-regular fa-bookmark"></i></button></a>
        <button v-if="currentUser" :aria-label="article.viewerState?.liked ? 'Unlike this article' : 'Like this article'" v-on:click="handleLikeClick(article.id)"><i class="fa fa-thumbs-up fa-regular" v-bind:class="{'i-changed': article.viewerState?.liked}"></i></button>
        <button v-if="currentUser" :aria-label="article.viewerState?.disliked ? 'Undislike this article' : 'Dislike this article'" v-on:click="handleDislikeClick(article.id)"><i class="fa-regular fa-thumbs-down" v-bind:class="{'i-changed': article.viewerState?.disliked}"></i></button>
        <button v-if="currentUser" :aria-label="article.viewerState?.savedForLater ? 'Unsave this article' : 'Save this article'" v-on:click="handleSaveForLaterClick(article.id)"><i class="fa fa-regular fa-bookmark" v-bind:class="{'i-changed': article.viewerState?.savedForLater}"></i></button>
      </div>
    </div>
  </div>
  
  <!-- Column 2 -->
  <div class="column">
    <div class="article" v-for="article in articlesColumn2" v-bind:key="article.id">
      <div>
        <img class="article-image" v-lazy="gridArticleThumb(article.imageUrl)" :alt="article.title || 'News article'" preload="auto">
        <p class="category">published: {{ formatPublished(article.publishedAt) }}</p>
      </div>
      <a :href="article.urlPath" @click.prevent="pushToArticle(article.urlPath)">
        <h3 class="title" v-on:click="postClicked=article.id; logArticleClick()">{{article.title}}</h3>
      </a>
      <div
        v-if="getArticleSentimentToken(article) || getArticleLeanToken(article)"
        class="article-intel-row"
      >
        <span
          v-if="getArticleSentimentToken(article)"
          class="article-intel-chip"
          :style="getChipStyle(getArticleSentimentToken(article))"
          :aria-label="`Tone: ${getArticleSentimentToken(article).label}`"
        >
          <i :class="['fa', getArticleSentimentToken(article).icon, 'article-intel-chip__icon']" aria-hidden="true"></i>
          {{ getArticleSentimentToken(article).label }}
          <span v-if="isMockSentiment(article)" class="article-intel-chip__mock" aria-hidden="true" title="Dev preview">·</span>
        </span>
        <span
          v-if="getArticleLeanToken(article)"
          class="article-intel-chip article-intel-chip--bias"
          :style="getChipStyle(getArticleLeanToken(article))"
:aria-label="getFramingChipAria(article)"
          >
            <span class="article-intel-chip__prefix" aria-hidden="true">Framing:</span>
            {{ getDisplayBias(article) }}
            <span v-if="isMockBias(article)" class="article-intel-chip__mock" aria-hidden="true" title="Dev preview">·</span>
          </span>
      </div>
      <div class="buttons">
        <a href="/register" aria-label="Create a free account to like articles"><button aria-label="Like this article" v-if="!currentUser"><i class="fa fa-thumbs-up fa-regular"></i></button></a>
        <a href="/register" aria-label="Create a free account to dislike articles"><button aria-label="Dislike this article" v-if="!currentUser"><i class="fa fa-thumbs-down fa-regular"></i></button></a>
        <a href="/register" aria-label="Create a free account to save articles"><button aria-label="Save this article" v-if="!currentUser"><i class="fa fa-regular fa-bookmark"></i></button></a>
        <button v-if="currentUser" :aria-label="article.viewerState?.liked ? 'Unlike this article' : 'Like this article'" v-on:click="handleLikeClick(article.id)"><i class="fa fa-thumbs-up fa-regular" v-bind:class="{'i-changed': article.viewerState?.liked}"></i></button>
        <button v-if="currentUser" :aria-label="article.viewerState?.disliked ? 'Undislike this article' : 'Dislike this article'" v-on:click="handleDislikeClick(article.id)"><i class="fa-regular fa-thumbs-down" v-bind:class="{'i-changed': article.viewerState?.disliked}"></i></button>
        <button v-if="currentUser" :aria-label="article.viewerState?.savedForLater ? 'Unsave this article' : 'Save this article'" v-on:click="handleSaveForLaterClick(article.id)"><i class="fa fa-regular fa-bookmark" v-bind:class="{'i-changed': article.viewerState?.savedForLater}"></i></button>
      </div>
    </div>
  </div>
</div>
            
          <!-- ── Your Topics feed ─────────────────────────────────── -->
          <div v-if="currentUser" v-show="feedTab === 'your-topics'" class="topics-feed">
            <div v-if="topicsLoading" class="topics-feed__loading">
              <i class="fa-solid fa-spinner fa-spin"></i> Loading your topics feed…
            </div>
            <div v-else-if="topicsArticles.length === 0" class="topics-feed__empty">
              <i class="fa fa-solid fa-rss fa-2x" aria-hidden="true"></i>
              <p>Follow topics from the Sentiment Pulse to build your personal feed.</p>
            </div>
            <div v-else>
              <div class="topics-feed__header">
                <p class="topics-feed__eyebrow">Your Topics</p>
                <h3>Stories matched to what you follow</h3>
                <p>Filter by topic to jump straight into the conversations you care about.</p>
              </div>

              <div class="topics-filter-rail" aria-label="Filter your topics feed">
                <button
                  class="topics-filter-chip"
                  :class="{ 'topics-filter-chip--active': activeTopicFilter === null }"
                  @click="activeTopicFilter = null"
                >
                  All <span>{{ topicsArticles.length }}</span>
                </button>
                <button
                  v-for="topic in topicFilters"
                  :key="topic.name"
                  class="topics-filter-chip"
                  :class="{ 'topics-filter-chip--active': activeTopicFilter === topic.name }"
                  @click="activeTopicFilter = topic.name"
                >
                  {{ topic.name }} <span>{{ topic.count }}</span>
                </button>
              </div>

              <div v-if="filteredTopicsArticles.length === 0" class="topics-feed__empty">
                <i class="fa fa-filter fa-2x" aria-hidden="true"></i>
                <p>No articles matched this topic anymore. Try another filter.</p>
              </div>
              <ArticleGrid
                v-else
                :articles="filteredTopicsArticles"
                :currentUser="currentUser"
                :isDesktop="isDesktop"
                @like="handleLikeClick"
                @dislike="handleDislikeClick"
                @save="handleSaveForLaterClick"
                @navigate="pushToArticle"
                @article-click="onArticleClick"
                @unfollow-topic="unfollowTopicFromFeed"
              />
            </div>
          </div>
          </div>

          <ContentBasedSuggestionsSideBar v-show="feedTab === 'content-based' && !isDesktop"></ContentBasedSuggestionsSideBar>
          <div class="home-right-rail" v-if="isDesktop">
            <PersonalizedSidebar></PersonalizedSidebar>
          </div>
          </div>
      </div>
      <ScrollToTop></ScrollToTop>
      </div>
      <!-- bottom nav bar in mobile mode -->
     <BottomNavigation></BottomNavigation>
  </div>  
  
  </template>
  
  <script>
  
  import { watch } from 'vue';
  import { usePullToRefresh } from '@/composables/usePullToRefresh';
  import navBar from '../components/Navbar.vue';
  import MobileNavbar from '../components/MobileNavbar.vue'
  import ArticleService from '../services/ArticleService';
  import { optimizeImage } from '@/utils/imageOptimize';
  import ArticleDataService from '../services/ArticleDataService';
  import LoggerService from '@/services/LoggerService';
  import ContentBasedSuggestionsSideBar from '@/components/ContentBasedSuggestionsSideBar.vue';
  import BottomNavigation from '@/components/BottomNavigation.vue';
  import NotLoggedIn from '@/components/NotLoggedIn.vue';
  import { mapState } from 'vuex';
import AuthService from '@/services/AuthService';
import FeedIsLoading from '@/components/FeedIsLoading.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import { useHead } from '@vueuse/head';
import fallbackLogo from '@/images/coloredfinallogochanged.png';
import { useRoute } from 'vue-router';
import ScrollToTop from '@/components/ScrollToTop.vue';
import SentimentPulseDashboard from '@/components/SentimentPulseDashboard.vue';
import { getLeanToken, getSentimentToken } from '@/utils/coverageHelpers';
import ArticleGrid from '@/components/ArticleGrid.vue';
import PersonalizedSidebar from '@/components/PersonalizedSidebar.vue';
import TrendingStoriesRail from '@/components/TrendingStoriesRail.vue';
import {
  getFramingDisplayLabel,
  getFramingLeanToken,
  hasFramingFields,
} from '@/utils/framingHelpers';
import { evaluateWelcome, onPassiveDestinationReady, offerPageTour } from '@/composables/useTutorialProgress';
  


  export default {
    name: "homePage",
    components: {
      LoadingOverlay,
      navBar,
      MobileNavbar,
      ContentBasedSuggestionsSideBar,
      BottomNavigation,
      NotLoggedIn,
      FeedIsLoading,
      ScrollToTop,
      SentimentPulseDashboard,
      ArticleGrid,
      PersonalizedSidebar,
      TrendingStoriesRail,
    },
    setup() {
      const route = useRoute();
      const canonicalUrl = `https://www.barbershoptalk.com${route.fullPath}`;
      const title = "BarbershopTalk | News Intelligence. Every Perspective, No Filter.";
      const description = "BarbershopTalk shows you what's trending, how articles lean, what your side might be missing, and what every perspective is saying. News intelligence, sentiment analysis, media bias, and Daily Cut briefings in one place.";
      const image = fallbackLogo;

      useHead({
        title,
        link: [
          {
            rel: "canonical",
            href: canonicalUrl
          }
        ],
        script: [
          {
            type: 'application/ld+json',
            content: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Organization",
                  "@id": "https://www.barbershoptalk.com/#organization",
                  "name": "Barbershop Talk",
                  "url": "https://www.barbershoptalk.com/",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://res.cloudinary.com/drjcrfz85/image/upload/v1731803310/favicon_ol2wl4.webp"
                  }
                },
                {
                  "@type": "WebSite",
                  "@id": "https://www.barbershoptalk.com/#website",
                  "url": "https://www.barbershoptalk.com/",
                  "name": "Barbershop Talk",
                  "description": description,
                  "publisher": {
                    "@id": "https://www.barbershoptalk.com/#organization"
                  },
                  "potentialAction": {
                    "@type": "SearchAction",
                    "target": "https://www.barbershoptalk.com/searchArticles/{search_term_string}",
                    "query-input": "required name=search_term_string"
                  }
                }
              ]
            })
          }
        ],
        meta: [
          { name: "robots", content: "index,follow" },
          { name: "description", content: description },
          { property: "og:type", content: "website" },
          { property: "og:title", content: title },
          { property: "og:description", content: description },
          { property: "og:image", content: image },
          { property: "og:url", content: canonicalUrl },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:title", content: title },
          { name: "twitter:description", content: description },
          { name: "twitter:image", content: image },
        ]
      });

      return {};
    },
    data() {
      return {
      // //crsf token
      // crsf: '',
      isLoading: true,
      term: '',
      feedTab: 'news', // 'news' | 'content-based' | 'your-topics'
      //navbar toggle data property
      isDesktop: false,
      //articles array
      articles: [],
      //curatedContentBasedArticles: [],
      start: 0,
      end: 100,
      //trending articles array
      trendingArticles: [],
      // your-topics feed
      topicsArticles: [],
      topicsLoading: false,
      activeTopicFilter: null,
      //toggling mobile articles
      newsOrTrendingClicked: true,
      postClicked: 0,
      currentUserName: '',
        articleId: 0,
        ptrIsRefreshing: false
      }
    },
    beforeMount() {
      console.log('⚡️ beforeMount called');
     ArticleService.getAllArticles().then(response => {
      this.isLoading = true;
      this.articles = response.data
      this.isLoading = false;
     }).catch(error => {
    console.error('Failed to load articles:', error);
    console.error('Error details:', error.response);
    this.isLoading = false;
  });
     if(this.currentUser > 0){
      ArticleDataService.getUsername().then(response => {
            this.currentUsername = response.data
      }),
    ArticleDataService.getUser().then(response => {
          this.currentUser = response.data
    })
     }
     
    //calls all of the trending articles
    ArticleService.listTrendingArticles().then(response => {
      this.trendingArticles = response.data
      console.log(JSON.stringify(this.trendingArticles))
    });
  },  
    mounted() {
      if(this.$store.state.user.id > 0){
      if (this.$store.state.curatedContentBasedArticles.length === 0) {
      this.$store.dispatch('fetchCuratedArticles', this.$store.state.user.id)
    }
  }
      //depending on size renders the correct navbar
      this.isDesktop = window.matchMedia("(min-width: 913px)").matches
      window.addEventListener("resize", this.handleResize)
      window.addEventListener('scroll', this.handleScroll)

      const scrollDepth = this.$store.state.scrollDepth;
  if (scrollDepth !== null) {
    console.log("depth: " + scrollDepth);
    setTimeout(() => {
      window.scrollTo(0, scrollDepth);
    }, 1000); 
  }
  
  // Wire pull-to-refresh
  const ptr = usePullToRefresh({
    onRefresh: async () => {
      await this.refresh();
    },
    scrollRoot: window,
    threshold: 80
  });
  this._pullPtr = ptr;
  this._stopPullRefreshingWatch = watch(
    ptr.isRefreshing,
    (v) => {
      this.ptrIsRefreshing = v;
    },
    { immediate: true }
  );
  ptr.attachPullListeners();

  this.$nextTick(() => {
    setTimeout(() => {
      try {
        evaluateWelcome({ homeReady: true });
        onPassiveDestinationReady('home');
        setTimeout(() => {
          try {
            offerPageTour('home');
          } catch (e) {
            /* tutorial must never break Home */
          }
        }, 900);
      } catch (e) {
        /* tutorial must never break Home */
      }
    }, 320);
  });
      
},
  computed: {
    ...mapState({
      currentUser: state => state.user.id,
      curatedContentBasedArticles: state => state.curatedContentBasedArticles
    }),
  isProTier(){
    return ['PRO', 'ELITE'].includes(this.$store.state.entitlements?.tier);
  },
  //articles to display until scroll
  displayArticles(){
    if (this.feedTab === 'content-based' && this.currentUser){
      return this.curatedContentBasedArticles.slice(this.start, this.end);
    }
    return this.articles.slice(this.start, this.end);
  
  },  
  articlesColumn1() {
    return this.displayArticles.filter((_, index) => index % 2 === 0);
  },
  articlesColumn2() {
    return this.displayArticles.filter((_, index) => index % 2 !== 0);
  },
  topicFilters() {
    const counts = new Map();
    this.topicsArticles.forEach((article) => {
      (article.matchedTopics || []).forEach((topic) => {
        counts.set(topic, (counts.get(topic) || 0) + 1);
      });
    });
    return Array.from(counts, ([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  },
  filteredTopicsArticles() {
    if (!this.activeTopicFilter) return this.topicsArticles;
    return this.topicsArticles.filter(article =>
      (article.matchedTopics || []).includes(this.activeTopicFilter)
    );
  }

},
methods: {
  gridArticleThumb(url) {
    return optimizeImage(url, 280, 158)
  },

  // ── Dev-only mock intel helpers (same seed formula as ArticleCard) ──
  _devSeed(article) {
    return Number(article.id) || 0;
  },
  getMockSentiment(article) {
    if (article.sentimentScore != null) return null;
    if (process.env.NODE_ENV !== 'development') return null;
    const s = this._devSeed(article);
    return ((s * 9301 + 49297) % 233280) / 233280 * 100;
  },
  getMockBias(article) {
    if (hasFramingFields(article)) return null;
    if (article.biasLabel != null) return null;
    if (process.env.NODE_ENV !== 'development') return null;
    const labels = ['Left-Leaning', 'Center', 'Right-Leaning'];
    return labels[this._devSeed(article) % labels.length];
  },
  getDisplaySentiment(article) {
    const real = article.sentimentScore;
    return real != null ? Number(real) : this.getMockSentiment(article);
  },
  getDisplayBias(article) {
    // Prefer canonical framing when present; never invent from categorySpecified.
    if (hasFramingFields(article)) {
      return getFramingDisplayLabel(article) || article.framingLabel || article.biasLabel;
    }
    return article.biasLabel ?? this.getMockBias(article);
  },
  isMockSentiment(article) {
    return article.sentimentScore == null && this.getMockSentiment(article) != null;
  },
  isMockBias(article) {
    return !hasFramingFields(article)
      && article.biasLabel == null
      && this.getMockBias(article) != null;
  },
  getArticleSentimentToken(article) {
    return getSentimentToken(this.getDisplaySentiment(article));
  },
  getArticleLeanToken(article) {
    if (hasFramingFields(article)) {
      return getFramingLeanToken(article);
    }
    return getLeanToken(this.getDisplayBias(article));
  },
  getFramingChipAria(article) {
    return `Framing: ${this.getDisplayBias(article) || 'unknown'}`;
  },
  getChipStyle(token) {
    if (!token) return {};
    return {
      color: token.color,
      background: token.bg,
      border: `1px solid ${token.border}`,
    };
  },
  formatPublished(publishedAt) {
    if (!publishedAt) return '';
    const now = Date.now();
    const then = new Date(publishedAt).getTime();
    const diffMs = now - then;
    if (Number.isNaN(then) || diffMs < 0) return '';
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay > 0) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    if (diffHr > 0) return `${diffHr} hour${diffHr > 1 ? 's' : ''} ago`;
    if (diffMin > 0) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    return 'just now';
  },
  /**
   * Pull-to-refresh / parent-driven reload. Same data sources as beforeMount.
   * Resets infinite-scroll window on success; on failure keeps list + pagination.
   */
  async refresh() {
    const prevStart = this.start;
    const prevEnd = this.end;
    this.start = 0;
    this.end = 100;
    
    try {
      const fetchTasks = [
        ArticleService.getAllArticles(),
        ArticleService.listTrendingArticles()
      ];
      
      // If logged in, also refresh curated feed (Vuex store)
      let curatedPromise = null;
      if (this.currentUser > 0) {
        curatedPromise = this.$store.dispatch('fetchCuratedArticles', this.currentUser);
        fetchTasks.push(curatedPromise);
      }
      
      const results = await Promise.allSettled(fetchTasks);
      const [articlesRes, trendingRes, curatedRes] = results;
      
      // Process articles
      if (articlesRes.status === 'fulfilled' && Array.isArray(articlesRes.value?.data)) {
        const rawArticles = articlesRes.value.data;
        const seen = new Set();
        const deduped = [];
        for (let i = 0; i < rawArticles.length; i += 1) {
          const article = rawArticles[i];
          if (!article || article.id == null) continue;
          const key = String(article.id);
          if (seen.has(key)) continue;
          seen.add(key);
          deduped.push(article);
        }
        this.articles = deduped;
      }
      
      // Process trending
      if (trendingRes.status === 'fulfilled' && Array.isArray(trendingRes.value?.data)) {
        this.trendingArticles = trendingRes.value.data;
      }
      
      // Log warnings for failures
      if (articlesRes.status === 'rejected') {
        console.warn('[Home] articles fetch failed:', articlesRes.reason);
      }
      if (trendingRes.status === 'rejected') {
        console.warn('[Home] trending fetch failed:', trendingRes.reason);
      }
      if (curatedRes && curatedRes.status === 'rejected') {
        console.warn('[Home] curated articles fetch failed:', curatedRes.reason);
      }
      
    } catch (err) {
      console.error('[Home] refresh failed:', err);
      this.start = prevStart;
      this.end = prevEnd;
    }
  },
  googleValidate(token) {
      console.log("token:" + token);
      return AuthService.loginGoogleValidate(token)
        .then(response => {
          console.log('Response from backend from frontend:', response.data);
          
          if (token) {
            this.$store.commit("SET_AUTH_TOKEN", response.data.token);
            this.$store.commit("SET_USER", response.data.user);
            this.$store.dispatch('fetchCuratedArticles', response.data.user.id).then(() => {
              this.$router.push('/'); // Redirect to the main page
              // Refresh the page to remove the Google URL parameters
              window.location.href = window.location.origin;
            });
          } else {
            console.error('Unexpected response structure:', response.data);
            throw new Error('Invalid response structure');
          }
        })
        .catch(error => {
          console.log(error.response)
          console.log(error.response.status)
      if (error.response && error.response.status === 404) {
        // Redirect to registration if user not found
        AuthService.registerGoogleValidate(token)
    .then(response => {
        if (response.data.newUser) {
          
          this.$router.push('/login');
        } else {
          
          this.$router.push('/login');
        }
      })
      .catch(error => {
        console.error('Google Sign-In error:', error);
        alert("Failed to register, please try again.")
      });
        this.$router.push({ name: 'login', query: { email: error.response.data.email } });
      } else {
        console.error('Google Sign-In error:', error.response ? error.response.data : error.message);
        this.invalidCredentials = true; // Set this to show an error message
      }
    });
    },
  
    //handles resize to toggle navbars
    handleResize() {
      this.isDesktop = window.matchMedia("(min-width: 913px)").matches
      this.newsOrTrendingClicked = true
    },
    handleScroll() {
     let scrollY = window.scrollY;
     let innerHeight = window.innerHeight;
     let docHeight = document.body.offsetHeight;
     if (scrollY + innerHeight >= docHeight * 0.4) {
       this.end+=12
     }
    },
    articleLookupId(article) {
      return Number(article?.id ?? article?.articleId);
    },
    findArticlesById(articleId) {
      const id = Number(articleId);
      if (!Number.isFinite(id)) return [];
      const lists = [
        this.articles,
        this.curatedContentBasedArticles,
        this.trendingArticles,
        this.topicsArticles,
      ];
      const matches = [];
      for (const list of lists) {
        if (!Array.isArray(list)) continue;
        for (const article of list) {
          if (this.articleLookupId(article) === id) matches.push(article);
        }
      }
      return matches;
    },
    applyViewerState(articles, nextState) {
      articles.forEach((article) => {
        article.viewerState = { ...nextState };
      });
    },
    restoreViewerState(articles, prevStates) {
      articles.forEach((article, index) => {
        article.viewerState = prevStates[index];
      });
    },
    async fetchTopicsFeed() {
      if (!this.currentUser) return;
      this.topicsLoading = true;
      try {
        const res = await ArticleDataService.getMyTopicsFeed();
        this.topicsArticles = Array.isArray(res.data) ? res.data : [];
        if (this.activeTopicFilter && !this.topicFilters.some(t => t.name === this.activeTopicFilter)) {
          this.activeTopicFilter = null;
        }
      } catch {
        this.topicsArticles = [];
      } finally {
        this.topicsLoading = false;
      }
    },
    setFeedTab(tab) {
      if (!this.currentUser && tab !== 'news') {
        this.feedTab = 'news';
        return;
      }

      this.feedTab = tab;

      if (tab === 'your-topics' && this.topicsArticles.length === 0 && !this.topicsLoading) {
        this.fetchTopicsFeed();
      }
    },
    onArticleClick(articleId) {
      this.postClicked = articleId;
      this.logArticleClick();
    },
    async unfollowTopicFromFeed(tag) {
      try {
        await ArticleDataService.unfollowTopic(tag);
      } catch (err) {
        if (err?.response?.status !== 404) {
          console.error('Error unfollowing topic from feed', tag, err);
          return;
        }
      }

      this.topicsArticles = this.topicsArticles
        .map(article => ({
          ...article,
          matchedTopics: (article.matchedTopics || []).filter(topic => topic !== tag),
        }))
        .filter(article => article.matchedTopics.length > 0);

      if (this.activeTopicFilter === tag) {
        this.activeTopicFilter = null;
      }
    },
    handleLikeClick(articleId) {
      const articles = this.findArticlesById(articleId);
      if (!articles.length) return;

      const article = articles[0];
      const prevStates = articles.map(item => (item.viewerState ? { ...item.viewerState } : null));
      const turningOn = !article.viewerState?.liked;
      this.applyViewerState(articles, {
        ...(article.viewerState || {}),
        liked: turningOn,
        ...(turningOn ? { disliked: false } : {}),
      });

      ArticleDataService.likeAPost({ liked: true }, articleId)
        .then(() => {
          if (prevStates[0]?.disliked) {
            ArticleDataService.dislikeAPost({ disliked: true }, articleId).catch(() => {});
          }
          const sessionId = localStorage.getItem('sessionId');
          LoggerService.logger({ user_id: this.currentUser, articleId, event: 'like', sessionId }).catch(() => {});
        })
        .catch(err => {
          this.restoreViewerState(articles, prevStates);
          console.error('Error toggling like for article', articleId, err);
        });
    },
    handleDislikeClick(articleId) {
      const articles = this.findArticlesById(articleId);
      if (!articles.length) return;

      const article = articles[0];
      const prevStates = articles.map(item => (item.viewerState ? { ...item.viewerState } : null));
      const turningOn = !article.viewerState?.disliked;
      this.applyViewerState(articles, {
        ...(article.viewerState || {}),
        disliked: turningOn,
        ...(turningOn ? { liked: false } : {}),
      });

      ArticleDataService.dislikeAPost({ disliked: true }, articleId)
        .then(() => {
          if (prevStates[0]?.liked) {
            ArticleDataService.likeAPost({ liked: true }, articleId).catch(() => {});
          }
          const sessionId = localStorage.getItem('sessionId');
          LoggerService.logger({ user_id: this.currentUser, articleId, event: 'dislike', sessionId }).catch(() => {});
        })
        .catch(err => {
          this.restoreViewerState(articles, prevStates);
          console.error('Error toggling dislike for article', articleId, err);
        });
    },
    handleSaveForLaterClick(articleId) {
      const articles = this.findArticlesById(articleId);
      if (!articles.length) return;

      const article = articles[0];
      const prevStates = articles.map(item => (item.viewerState ? { ...item.viewerState } : null));
      this.applyViewerState(articles, {
        ...(article.viewerState || {}),
        savedForLater: !article.viewerState?.savedForLater,
      });

      ArticleDataService.saveForLater({ saveForLater: true }, articleId)
        .then(() => {
          const sessionId = localStorage.getItem('sessionId');
          LoggerService.logger({ user_id: this.currentUser, articleId, event: 'save_for_later', sessionId }).catch(() => {});
        })
        .catch(err => {
          this.restoreViewerState(articles, prevStates);
          console.error('Error toggling save for later for article', articleId, err);
        });
    },
  //in mobile, this renders the pages articles or the trending articles
  toggleTrendingAndNews(){
        if(this.newsOrTrendingClicked){
        this.newsOrTrendingClicked = false;
        } else {
            this.newsOrTrendingClicked = true;
        }
    },
    logArticleClick(){
      const sessionId = localStorage.getItem("sessionId");
        const event = {
          user_id: this.currentUser,
          articleId: this.postClicked,
          event: `read:article`,
          sessionId: sessionId
        }
        console.log(sessionId)

        LoggerService.logger(event).then(response => {
          if(response.status == 200){

            console.log('title logged')
          } else {
            console.log('not working')
          }
        })
    },
    pushToArticle(pathOrCategory, title, id){
      this.$store.state.scrollDepth = window.scrollY;
      // Preferred path for DTO v1: pass server-computed urlPath.
      if (typeof pathOrCategory === 'string' && pathOrCategory.startsWith('/')) {
        if (this.$route.path !== pathOrCategory) {
          this.$router.push({ path: pathOrCategory });
        }
        return;
      }
      // Temporary compatibility for components still emitting legacy args.
      const category = pathOrCategory;
      const articleTitle = (title || '')
        .toLowerCase()
        .replace(/\W+/g, '-')
        .replace(/^-|-$/g, '');
      const fallbackPath = category !== null
        ? `/${category}/${articleTitle}/${id}`
        : `/business/${articleTitle}/${id}`;
      if (this.$route.path !== fallbackPath) {
        this.$router.push({ path: fallbackPath });
      }
    },
    searchArticles(term){
      console.log(term)
      this.$router.push({
        path: `/searchArticles/${term}`,
      })
    }
},
  //checks what size the window is on load
    beforeUnmount() {
      window.removeEventListener("resize", this.handleResize)
      window.removeEventListener("scroll", this.handleScroll)
      this._stopPullRefreshingWatch?.();
      this._pullPtr?.detachPullListeners();
      if (this.$store.state.curatedContentBasedArticles.length !== 0) {
      this.$store.dispatch('fetchCuratedArticles', this.$store.state.user.id)
    }
  },
  beforeRouteLeave(to, from, next) {
    localStorage.setItem('feedScrollPosition', window.scrollY);
    next();
  }
  }
  
  
  
  </script>
  
  <style scoped>
    a{
         text-decoration: none;
         color: black;
         transition: all 0.3s ease;
         font-family: 'Titillium Web', sans-serif;
       }
       a:hover{
         color: #3088d4;
       }
       .hidden {
           display: none;
       }
     
       i {
         margin: 9px 5px 0px 5px;
         color: #0b7ee3;
       }
       i:hover{
         color: #dd3434;
       }
     
       .i-changed {
         color:#dd3434
       }
       .search-bar{
       margin-top: 10px;
       background: #ffffff;
   border: none;
   border: 1px solid #e5e5e5;
   color: #262626;
   width: calc(100% - 70px);
   padding: 8px 35px 8px 23px;
   font-size: 16px;
   background-position: right 8px;
   background-repeat: no-repeat;
   border-radius: 60px;
   box-shadow: 0px 0px 25px rgba(0,0,0,  0);
   }
   
   .fa-magnifying-glass{
           float: right;
           margin-right: 15px;
           margin-top: -18px;
           position: relative;
           z-index: 2;
   }
   
       .trending-nav{
       display: none;
     }
     
       button{
         background: none;
         border: none;
         
         cursor: pointer;
       }
       
       .container {
           display: flex;
           flex-direction: column;
           box-sizing: border-box;
       }

       @media (max-width: 913px) {
        .container {
          padding-bottom: calc(84px + env(safe-area-inset-bottom, 0px));
        }
       }
  .pulse-section {
    max-width: 1200px;
    margin: 0 auto 24px;
    padding: 0 16px;
  }

  .home-main-content-column {
    flex: 1 1 0;
    min-width: 0;
  }

  .home-right-rail {
    flex-shrink: 0;
    width: 20vw;
    min-width: 200px;
    max-width: 280px;
    overflow: hidden;
  }

  @media (max-width: 1100px) {
    .home-right-rail {
      display: none;
    }
  }

   .main-area{
         display: block;
         height: 100%;
       }
       @media (min-width: 768px) { 
        .main-area {
          display: flex; 
        }
        }
       /* TRENDING */
     
       #trending{
     flex-basis: 16rem;
     flex-shrink: 0;
       align-items: center;
       border: 2px solid #c3c5cc;
       font-weight: 900;
   }
     
     .trending-articles{
     font-family: 'Titillium Web', sans-serif;
     font-size: larger;
     margin-bottom: 20px;
     border-top: .1px solid #3088d4;
     background-color: #f4f1f1;
     }
     
     .trending-title{
         margin-top: 10px;
         cursor: pointer;
         font-size: large;
       }
       .trending-published{
         margin: 3px;
         background: #e4e6eb;
         font-size: small;
         text-decoration: none;
       }
     
       .trending-data{
         display: flex;
         justify-content: space-evenly;
         align-items: baseline;
         margin: 0px;
       }
     
       .trending-category{
         margin: 0px;
         font-size: medium;
       }
     
       .trending-like-count{
         margin: 0px;
         font-size: medium;
       }
       
       /* MAIN NEWS GRID */
       .news-grid{
         height: fit-content;
         padding: 3% 3% 3% 3%;
         display: grid;
         grid-template-columns: repeat(4, minmax(0, 1fr));
         grid-auto-rows: minmax(250px, auto);
         grid-auto-flow: dense;
         gap: 20px;
         background-color: #eeeff1;
         align-content: center;
       }
     
       @media (max-width: 913px) {
  .news-grid {
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    padding: 3vw 4vw 3vw 0vw;
  }
  }
   
   @media (max-width: 649px) {
   .news-grid {
     grid-template-columns: 1fr 1fr;
     gap: 15px;
     align-items: center;
     width: auto;
   
   }
   .article{
         width: 160px !important;
       }
     
     @media (min-width: 100px) and (max-width: 913px) {
       /* TRENDING ON MOBILE */
     
       /* TRENDING NAV BOTTOM BAR */
       /* DISPLAYED AT 2 COLUMN LAYOUT BELOW */
     
       #trending{
         display: none;
       }
       .trending-nav{
         background-color: #fff;
        z-index: 1100;
         position: fixed;
         bottom: 0;
        left: 0;
        right: 0;
         width: 100%;
         
       }
     
       .trending-nav{
         display: flex;
         justify-content: space-around;
         border-top: 2px solid #0b7ee3;
         border-radius: 4%;
       }
     
       .news-grid-trending{
         width: 100vw;
         height: fit-content;
         padding: 3% 3% 3% 3%;
         display: grid;
         grid-template-columns: 1fr 1fr;
         gap: 15px;
         padding: 3vw 4vw 3vw 0vw;
         grid-auto-flow: dense;
         gap: 20px;
         background-color: #eeeff1;
         align-content: center;
       }
       .trending-nav:hover{
         border-top: 2px solid #dd3434;
       }
       .trending-nav-list{
         display: flex;
         justify-content: space-evenly;
         list-style-type: none;
       }
       .trending-nav-news-button-clicked{
         color: #dd3434;
       }
       .trending-nav-search-button:hover{
         color: #dd3434;
       }
     
       .trending-nav-trending-button:hover{
         color: #dd3434;
       }
   
       .trending-nav-profile-button:hover{
         color: #dd3434;
       }
     }
   
     }
     
     
       /* ARTICLE */
     
       .article{
       padding-bottom: 20px;
       border-bottom: 1px solid #dd3434;
       margin-left: 5%;
       margin-bottom: 15px;
       height: fit-content;
       width: 80%;
       background-color: #fff;
       text-align: center;
       transition: all 0.3s ease;
       animation: fadeInBottom 0.5s ease-in;
     }
     
     @keyframes fadeInBottom {
       from {
           transform: translateY(100%);
           opacity: 0;
       }
       to {
           transform: translateY(0);
           opacity: 1;
       }
   }
     
     
       .article:hover,
       .article.active{
         border-bottom: 2px solid #0b7ee3;
         background-color: #eef6ff;
         color: #3088d4;
         transition: border-bottom 0.3s ease, color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
         transform: scale(1.01);
         box-shadow: 0 8px 22px rgba(11, 126, 227, 0.18);
         opacity: 1;
       }

       .article:hover a,
       .article.active a {
         color: #3088d4;
       }
     
       .article-image{
         height: 57%;
         width:100%;
         aspect-ratio: 16/9;
         object-fit: cover;
       }
     
       .title{
         margin: 0px;
         cursor: pointer;
       }
      
     .buttons{
       display: flex;
       justify-content: center;
     }

     @media (max-width: 913px){
     .buttons{
       display: flex;
       justify-content: center;
     }
   }
       
       .category{
         font-size: 5%;
         display: inline-block;
         margin: 0px;
         z-index: 1;
         background: #e4e6eb;
         border-radius: 10%;
         padding: 2px;
         text-align: center;
       }

      /* ── Sentiment + bias intel chips (raw article loops) ── */
      .article-intel-row {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 5px;
        padding: 6px 8px 2px;
      }

      .article-intel-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 10px;
        border: 1px solid transparent;
        border-radius: 999px;
        font-size: 0.68rem;
        font-weight: 800;
        letter-spacing: 0.025em;
        line-height: 1.3;
        white-space: nowrap;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.66);
      }

      .article-intel-chip__icon,
      .article-intel-chip__icon:hover {
        margin: 0 3px 0 0;
        color: currentColor;
        font-size: 0.6rem;
      }

      .article-intel-chip__prefix {
        font-weight: 700;
        opacity: 0.75;
      }

      .article-intel-chip__mock {
        font-size: 0.6rem;
        opacity: 0.45;
        cursor: help;
        margin-left: 1px;
      }

      @media (max-width: 649px) {
        .article-intel-chip {
          font-size: 0.62rem;
          padding: 2px 7px;
        }
      }
       .toggle-main-content-right {
  position: fixed; 
  top: 50%;
  right: 1px; 
  transform: translateY(-50%); 
  background-color: #fff;
  border-radius: 50%;
  z-index: 10; 
  cursor: pointer;
}
   
     .toggle-main-content-right .fa-arrow-right:before {
       background-color: #fff;
       border-radius: 50%;
     }
     .toggle-main-content-left {
  position: fixed; 
  top: 50%; 
  left: 1px; 
  transform: translateY(-50%); 
  background-color: #fff;
  border-radius: 50%;
  z-index: 10;
  cursor: pointer;
}
   
     .fa-arrow-left:before{
       background-color: #fff;
       border-radius: 50%;
     }
  
     @media (max-width: 1300px) {
  .news-grid{
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 1%;
  }
  
  .toggle-main-content-right .fa-arrow-right:before {
    font-size: larger;
  }

  .toggle-main-content-left .fa-arrow-left:before {
    font-size: larger;
  }
    
  
  }
  @media (max-width: 1024px) {
  .news-grid{
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 1%;
  }
  
  #trending{
    flex-basis: 13em
  }
  }
  @media (max-width: 1023px) {
    .news-grid{
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 1%;
  }
  }
  @media (max-width: 693px) {
  
  .toggle-main-content-right .fa-arrow-right:before {
    font-size: medium;
  }

  .toggle-main-content-left .fa-arrow-left:before {
    font-size: medium;
  }
  button i {
    font-size: small;
  }
  .title{
    font-size: large;
  }
  }

  @media (max-width: 649px) {
  .column {
    flex: 0 1 100%; 
  }
  .news-grid {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px; 
}

.column {
  flex: 0 1 calc(50% - 10px); 
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.article {
  box-sizing: border-box;
  transition: transform 0.3s ease;
  margin-left: 5%;
}
}
@media screen and (max-width: 398px) {

.news-grid{
  padding: 10px !important;
}

.article{
  margin-left: 0%;
}

}

/* REMOVED: .loading-overlay styles are now in LoadingOverlay component */

/* ── PULL-TO-REFRESH INDICATOR ────────────────────────── */
.pull-refresh-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0;
  color: #3a6ed7;
  font-size: 16px;
  font-weight: 600;
  background: #f8faff;
  border-bottom: 1px solid #e2e8f0;
}

.pull-refresh-indicator .fa-spinner {
  font-size: 18px;
}

.refresh-text {
  font-size: 14px;
  color: #64748b;
}

.hidden{
  display: none;
}

/* ── Feed tabs ───────────────────────────────────────────────── */
.home-feed-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  background: #f8faff;
  border-bottom: 1px solid #e2e8f0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  position: sticky;
  top: 0;
  z-index: 50;
}

.home-feed-tabs::-webkit-scrollbar {
  display: none;
}

.home-feed-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 34px;
  padding: 7px 16px;
  border-radius: 999px;
  border: 1.5px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s, color 0.18s, border-color 0.18s;
}

.home-feed-tab--active {
  background: #1a56db;
  color: #fff;
  border-color: #1a56db;
}

.home-feed-tab--cta {
  border-color: rgba(221, 52, 52, 0.28);
  background: linear-gradient(135deg, #fff7f7 0%, #ffffff 100%);
  color: #b91c1c;
  font-weight: 800;
  text-decoration: none;
}

.home-feed-tab--cta:hover {
  border-color: #dd3434;
  color: #dd3434;
}

/* ── Your Topics feed panel ──────────────────────────────────── */
.topics-feed {
  background: #eeeff1;
}

.topics-feed__loading,
.topics-feed__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 20px;
  color: #6b7280;
  font-size: 0.9rem;
  text-align: center;
}

.topics-feed__empty p {
  margin: 0;
  max-width: 260px;
  line-height: 1.5;
}

.topics-feed__header {
  padding: 16px 18px 10px;
  background: linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%);
  border-bottom: 1px solid #dbeafe;
}

.topics-feed__eyebrow {
  margin: 0 0 4px;
  color: #0b7ee3;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.topics-feed__header h2,
.topics-feed__header h3 {
  margin: 0;
  color: #111827;
  font-size: 1.05rem;
  font-weight: 900;
  font-family: 'Titillium Web', sans-serif;
}

@media (min-width: 913px) {
  .home-feed-tabs {
    position: static;
    justify-content: center;
    margin: 0 0 16px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 18px;
    background:
      radial-gradient(circle at top left, rgba(26, 86, 219, 0.08), transparent 34%),
      linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  }

  .topics-feed {
    overflow: hidden;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 20px;
    background: #ffffff;
    box-shadow: 0 16px 38px rgba(15, 23, 42, 0.07);
  }
}

.topics-feed__header p:not(.topics-feed__eyebrow) {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.4;
}

.topics-filter-rail {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.topics-filter-rail::-webkit-scrollbar {
  display: none;
}

.topics-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  min-height: 32px;
  padding: 6px 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #374151;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s, color 0.18s, border-color 0.18s, transform 0.15s;
}

.topics-filter-chip:hover {
  transform: translateY(-1px);
  border-color: #0b7ee3;
}

.topics-filter-chip span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #eef2ff;
  color: #0b65c2;
  font-size: 0.68rem;
}

.topics-filter-chip--active {
  background: #0b7ee3;
  color: #fff;
  border-color: #0b7ee3;
}

.topics-filter-chip--active span {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

/* ── Trending sidebar heading ───────────────────────── */
.trending-sidebar-heading {
  font-family: 'Titillium Web', sans-serif;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #374151;
  padding: 10px 12px 6px;
  margin: 0;
}

/* ── Homepage hero ──────────────────────────────────── */
.bst-hero {
  max-width: 1200px;
  margin: 0 auto 0;
  padding: 100px 16px 32px;
  text-align: center;
}

@media (min-width: 913px) {
  .bst-hero {
    padding-top: 140px;
    padding-bottom: 32px;
    padding-left: 20px;
    padding-right: 20px;
  }
}

.bst-hero__kicker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
  padding: 4px 12px;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.bst-hero__kicker i {
  margin: 0;
  font-size: 0.7rem;
  color: #1d4ed8;
}

.bst-hero__h1 {
  margin: 0 0 14px;
  font-family: 'Titillium Web', sans-serif;
  font-size: clamp(1.75rem, 4vw, 2.8rem);
  font-weight: 900;
  color: #111827;
  line-height: 1.15;
  letter-spacing: -0.01em;
}

.bst-hero__sub {
  margin: 0 auto 22px;
  max-width: 580px;
  font-size: clamp(0.88rem, 2vw, 1.05rem);
  color: #4b5563;
  line-height: 1.6;
}

.bst-hero__sub-line {
  display: block;
}

.bst-hero__tagline {
  margin: -8px auto 22px;
  color: #1f2937;
  font-size: clamp(0.82rem, 1.7vw, 0.96rem);
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.bst-hero__cta-label--mobile {
  display: none;
}

.pulse-intro__short {
  display: none;
}

.bst-hero__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
}

.bst-hero__cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  border-radius: 999px;
  font-size: 0.88rem;
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.18s, box-shadow 0.18s, background 0.18s;
  white-space: nowrap;
}

.bst-hero__cta i {
  margin: 0;
  font-size: 0.75rem;
  color: inherit;
}

.bst-hero__cta .fa-arrow-right:before {
  background: transparent;
  border-radius: 0;
}

.bst-hero__cta--primary {
  background: linear-gradient(135deg, #1a56db 0%, #0b7ee3 100%);
  color: #fff;
  box-shadow: 0 4px 16px rgba(11, 126, 227, 0.35);
}

.bst-hero__cta--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(11, 126, 227, 0.45);
  color: #fff;
}

.bst-hero__cta--ghost {
  background: transparent;
  color: #1a56db;
  border: 1.5px solid #bfdbfe;
}

.bst-hero__cta--ghost:hover {
  background: #eff6ff;
  color: #1a56db;
  transform: translateY(-1px);
}

.bst-hero__cta--ghost::after {
  content: "→";
}

.bst-hero__chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0;
}

.bst-hero__chips span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #374151;
  font-size: 0.78rem;
  font-weight: 700;
}

.bst-hero__chips span + span {
  margin-left: 8px;
  border-left-color: rgba(11, 126, 227, 0.32);
}

.bst-hero__chips span i {
  margin: 0;
  font-size: 0.72rem;
  color: #0b7ee3;
}

/* ── Pulse intro line ───────────────────────────────── */
.pulse-intro {
  margin: 0 auto 10px;
  max-width: 1200px;
  padding: 0 20px;
  font-size: 0.88rem;
  color: #4b5563;
  line-height: 1.55;
}

.pulse-intro strong {
  color: #111827;
}

/* ── Intelligence feature strip ─────────────────────── */
.home-intelligence-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 1200px;
  margin: 0 auto 20px;
  padding: 0 16px;
}

.home-intelligence-strip__card {
  flex: 1 1 240px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%);
  border: 1px solid #bfdbfe;
  text-decoration: none;
  color: #111827;
  transition: transform 0.18s, box-shadow 0.18s;
}

.home-intelligence-strip__card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(11, 126, 227, 0.15);
  color: #111827;
}

.home-intelligence-strip__icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.9rem;
  margin: 0;
}

.home-intelligence-strip__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}

.home-intelligence-strip__text strong {
  font-size: 0.88rem;
  font-weight: 900;
  color: #111827;
  font-family: 'Titillium Web', sans-serif;
}

.home-intelligence-strip__text span {
  font-size: 0.78rem;
  color: #64748b;
  line-height: 1.4;
}

.home-intelligence-strip__status {
  display: block;
  font-size: 0.74rem;
  color: #64748b;
  line-height: 1.4;
}

.home-intelligence-strip__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
  font-size: 0.78rem;
  font-weight: 800;
}

.home-intelligence-strip__card--picks a,
.home-intelligence-strip__actions a {
  color: #047857;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 1px;
  line-height: 1.2;
  font-weight: 800;
  font-size: 0.78rem;
}

.home-intelligence-strip__card--picks a:hover,
.home-intelligence-strip__actions a:hover {
  text-decoration-thickness: 2px;
}

.home-intelligence-strip__card--picks a:focus-visible,
.home-intelligence-strip__actions a:focus-visible {
  outline: 2px solid #047857;
  outline-offset: 3px;
  border-radius: 2px;
}

.home-intelligence-strip__arrow {
  flex-shrink: 0;
  color: #0b7ee3;
  font-size: 0.78rem;
  margin: 0;
}

/* ── Under Sniper picks card (green variant) ── */
.home-intelligence-strip__card--picks {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-color: #a7f3d0;
}
.home-intelligence-strip__card--picks:hover {
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.2);
}
.home-intelligence-strip__icon--picks {
  background: #d1fae5;
  color: #059669;
}
.home-intelligence-strip__arrow--picks {
  color: #059669;
}

/* Hero chip link wrapper */
.bst-hero__chip-link {
  text-decoration: none;
  display: inline-flex;
}
.bst-hero__chip-link span {
  margin-left: 8px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.bst-hero__chip-link:hover span {
  border-color: #10b981;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

/* ── Mobile / tablet top section (matches isDesktop breakpoint) ── */
@media (max-width: 912px) {
  .bst-hero {
    padding-top: calc(var(--bst-mobile-nav-offset) + 10px);
    padding-bottom: 16px;
  }

  .bst-hero__kicker {
    margin-bottom: 10px;
    font-size: 0.66rem;
  }

  .bst-hero__h1 {
    margin-bottom: 10px;
    font-size: clamp(1.45rem, 6vw, 2rem);
  }

  .bst-hero__sub {
    margin-bottom: 14px;
    font-size: 0.84rem;
    line-height: 1.45;
  }

  .bst-hero__sub-line--desktop {
    display: none;
  }

  .bst-hero__actions {
    align-items: stretch;
    width: 100%;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 14px;
    gap: 8px;
  }

  .bst-hero__cta {
    width: 100%;
    justify-content: center;
    padding: 9px 14px;
    font-size: 0.8rem;
    white-space: normal;
    text-align: center;
    line-height: 1.25;
  }

  .bst-hero__cta-label--desktop {
    display: none;
  }

  .bst-hero__cta-label--mobile {
    display: inline;
  }

  .bst-hero__chips {
    display: none;
  }

  .pulse-section {
    margin-bottom: 12px;
  }

  .pulse-intro {
    margin-bottom: 8px;
    padding: 0 14px;
    font-size: 0.8rem;
    line-height: 1.4;
  }

  .pulse-intro__full {
    display: none;
  }

  .pulse-intro__short {
    display: inline;
  }

  .home-intelligence-strip {
    display: none;
  }
}

     </style>
